/**
 * Hook useRepositoryChanges
 *
 * Escucha cambios locales en repositorios usando repositoryEvents.
 * Verdaderamente offline-first - los eventos se emiten localmente sin depender de Supabase.
 *
 * Características:
 * - Reacciona a cambios locales (INSERT, UPDATE, DELETE)
 * - Funciona offline (los eventos son locales)
 * - Compatible con web (Dexie) y mobile (SQLite)
 * - Se actualiza cuando el usuario modifica datos o cuando llega sync
 *
 * Uso básico:
 * ```tsx
 * function ListaAnimales() {
 *   const { animales, loading } = useAnimalesRepository();
 *
 *   return <ul>{animales.map(a => <li key={a.caravana}>{a.peso}kg</li>)}</ul>;
 * }
 * ```
 *
 * Uso con filtros:
 * ```tsx
 * const { animales } = useAnimalesRepository({
 *   where: animal => animal.ubicacion_id === ubicacionId
 * });
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { repositoryEvents, localRepositoryEvents, RepositoryChangeEvent } from '../services/storage/RepositoryChangeEventEmitter';
import { getRepositories } from '../services/storage';
import { Animal } from '../types/interfaces';

/**
 * Extrae las caravanas de un `record` de evento, soportando todas sus formas:
 * - string (id/caravana en DELETE)
 * - objeto (INSERT/UPDATE)
 * - array de strings (BULK_DELETE)
 * - array de objetos (BULK_INSERT)
 */
function extractCaravanas(record: RepositoryChangeEvent['record']): string[] {
  if (typeof record === 'string') return [record];
  if (Array.isArray(record)) {
    return record
      .map((item) => (typeof item === 'string' ? item : (item as Record<string, unknown>)?.caravana))
      .filter((c): c is string => typeof c === 'string');
  }
  const caravana = (record as Record<string, unknown>)?.caravana;
  return typeof caravana === 'string' ? [caravana] : [];
}

/**
 * El registry de `getRepositories()` usa claves camelCase de repositorio, no
 * nombres de tabla. Mapa para resolver el lookup de datos del hook (los
 * eventos SÍ se emiten con el nombre de tabla, así que la suscripción no
 * necesita este mapa).
 */
const TABLE_TO_REPO_KEY: Record<string, string> = {
  sync_acciones: 'syncAcciones',
  siembras_lote: 'siembraLote',
  puntos_control: 'puntoControl',
};

export interface UseRepositoryChangesOptions {
  /** Nombre de la tabla/repositorio a escuchar */
  table: string;
  /** Filtro para los resultados */
  where?: (item: any) => boolean;
  /** Límite de resultados */
  limit?: number;
  /** Campo para ordenar */
  sortBy?: keyof any;
  /** Orden ascendente o descendente */
  sortOrder?: 'asc' | 'desc';
}

export interface UseRepositoryChangesResult<T> {
  /** Datos actuales (reactivo a cambios) */
  data: T[];
  /** Indica si está cargando */
  loading: boolean;
  /** Último evento recibido */
  lastEvent: RepositoryChangeEvent | null;
  /** Función para recargar datos */
  refresh: () => Promise<void>;
}

/**
 * Hook genérico para escuchar cambios de cualquier repositorio
 */
export function useRepositoryChanges<T = any>(
  options: UseRepositoryChangesOptions
): UseRepositoryChangesResult<T> {
  const { table, where, limit, sortBy, sortOrder } = options;
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastEvent, setLastEvent] = useState<RepositoryChangeEvent | null>(null);
  const isMounted = useRef(true);

  // Función para cargar datos
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Obtener el repositorio (la clave del registry puede diferir del nombre de tabla)
      const repoKey = TABLE_TO_REPO_KEY[table] ?? table;
      const repo = getRepositories()[repoKey as keyof ReturnType<typeof getRepositories>];
      if (!repo || typeof repo.getAll !== 'function') {
        console.warn(`[useRepositoryChanges] Repositorio ${table} no encontrado o no tiene getAll`);
        return;
      }

      let items = await repo.getAll() as any[];

      // Aplicar filtro
      if (where) {
        items = items.filter(where);
      }

      // Ordenar
      if (sortBy) {
        items = [...items].sort((a: any, b: any) => {
          const aVal = a[sortBy];
          const bVal = b[sortBy];
          if (aVal == null) return sortOrder === 'asc' ? -1 : 1;
          if (bVal == null) return sortOrder === 'asc' ? 1 : -1;
          if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }

      // Aplicar límite
      if (limit) {
        items = items.slice(0, limit);
      }

      if (isMounted.current) {
        setData(items as T[]);
      }
    } catch (error) {
      console.error(`[useRepositoryChanges] Error cargando ${table}:`, error);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [table, where, limit, sortBy, sortOrder]);

  // Cargar datos iniciales
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Escuchar cambios del repositorio
  useEffect(() => {
    // El cleanup lo pone en false; en StrictMode (dev) el efecto se vuelve a
    // montar y sin esto el hook descartaría todos sus resultados.
    isMounted.current = true;
    const handleChange = (event: RepositoryChangeEvent) => {
      // event.table puede ser:
      // - Nombre de tabla Supabase (para tablas que se sincronizan)
      // - Nombre de tabla local (para tablas que NO se sincronizan, como 'animales')

      // Verificar si el evento es para la tabla que nos interesa
      // IMPORTANTE: Para tablas que no se sincronizan, el evento se emite con el nombre local
      if (event.table === table) {
        setLastEvent(event);
        fetchData(); // Recargar datos
      }
    };

    // Suscribirse a cambios de sincronización (Supabase)
    const unsubscribeSync = repositoryEvents.subscribe(handleChange);

    // Suscribirse a cambios locales (solo UI, no sincronizan con Supabase)
    const unsubscribeLocal = localRepositoryEvents.subscribe(handleChange);

    return () => {
      isMounted.current = false;
      unsubscribeSync();
      unsubscribeLocal();
    };
  }, [table, fetchData]);

  return {
    data,
    loading,
    lastEvent,
    refresh: fetchData
  };
}

/**
 * Hook especializado para animales
 * Escucha cambios locales en la tabla animales (offline-first)
 */
export function useAnimalesRepository(options?: {
  where?: (animal: Animal) => boolean;
  limit?: number;
  sortBy?: keyof Animal;
  sortOrder?: 'asc' | 'desc';
}) {
  return useRepositoryChanges<Animal>({
    table: 'animales',
    ...options
  });
}

/**
 * Hook para un animal específico por caravana
 */
export function useAnimalRepository(caravana?: string) {
  const [animal, setAnimal] = useState<Animal | null>(null);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  const fetchData = useCallback(async () => {
    if (!caravana) {
      setAnimal(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { animal: animalRepo } = getRepositories();
      const result = await animalRepo.get(caravana);
      if (isMounted.current) {
        setAnimal(result ?? null);
      }
    } catch (error) {
      console.error(`[useAnimalRepository] Error cargando animal ${caravana}:`, error);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [caravana]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    isMounted.current = true; // ver useRepositoryChanges: re-montaje de StrictMode
    const handleChange = (event: RepositoryChangeEvent) => {
      // Verificar si el cambio afecta a este animal
      if (event.table === 'animales') {
        // record puede ser:
        //  - string (id/caravana en DELETE)
        //  - objeto (INSERT/UPDATE)
        //  - array de strings (BULK_DELETE) o array de objetos (BULK_INSERT)
        const eventCaravanas = extractCaravanas(event.record);
        if (caravana && eventCaravanas.includes(caravana)) {
          fetchData();
        }
      }
    };

    const unsubscribe = repositoryEvents.subscribe(handleChange);

    return () => {
      isMounted.current = false;
      unsubscribe();
    };
  }, [caravana, fetchData]);

  return {
    animal,
    loading,
    refresh: fetchData
  };
}

/**
 * Hook para animales por ubicación
 */
export function useAnimalesByUbicacionRepository(ubicacionId?: string) {
  const { data: animales, ...rest } = useAnimalesRepository({
    where: ubicacionId ? (animal) => animal.ubicacion_id === ubicacionId : undefined
  });

  return {
    animales,
    count: animales.length,
    ...rest
  };
}

/**
 * Hook para múltiples animales por caravana
 */
export function useAnimalesByCaravanasRepository(caravanas: string[] = []) {
  const { data: animales, ...rest } = useAnimalesRepository({
    where: caravanas.length > 0
      ? (animal) => caravanas.includes(animal.caravana)
      : undefined
  });

  return {
    animales,
    ...rest
  };
}
